import React, { PureComponent, memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldTableCustomColumnIds from '../../../../constants/FieldTableCustomColumnIds';
import FieldModes from '../../../../constants/FieldModes';
import FieldTypes from '../../../../constants/FieldTypes';
import { filterHiddenTableColumnsSelector } from '../../../../lib/tableHelper';

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const StyledTH = styled.div`
  display: flex;
  box-sizing: border-box;
  font-size: ${(props) => props.fontSize}px;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  border-top: ${(props) => (props.borderTop ? props.borderTop : 'transparent')};
  border-right: ${(props) => (props.borderRight ? props.borderRight : 'transparent')};
  border-left: ${(props) => (props.borderLeft ? props.borderLeft : 'transparent')};
  border-bottom: ${(props) => (props.borderBottom ? props.borderBottom : 'transparent')};
  padding: ${(props) => props.padding}px;
  border-bottom: ${(props) => props.border};
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => {
    const tl = props.borderTopLeftRadius != null ? `${props.borderTopLeftRadius}px` : '0';
    const tr = props.borderTopRightRadius != null ? `${props.borderTopRightRadius}px` : '0';
    return `${tl} ${tr} 0 0`;
  }};
  resize: none;
  ${(props) => {
    if (props.width) {
      return `max-width: ${props.width}px; min-width: ${props.width}px;`;
    } else {
      return 'flex: 1;';
    }
  }}
  vertical-align: top;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  svg {
    font-size: 12px;
  }
`;

const StyledTitleWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
`;

const StyledTitle = styled.span`
  display: flex;
  flex: 1;
  display: inline-block;
`;

class TableHeader extends PureComponent {

  static propTypes = {

    id: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldType: PropTypes.string,

    enableNumber: PropTypes.bool,
    enableMenu: PropTypes.bool,

    columns: PropTypes.array,
    columnWidths: PropTypes.object,

    //Action handlers
    onColumnRef: PropTypes.func,

    //Styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,

    loadCompletedId: PropTypes.string,
    onLoadCompleted: PropTypes.func,

  }

  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidUpdate() {

    const { onLoadCompleted, loadCompletedId } = this.props;
    const { rowRef } = this.state;

    if (onLoadCompleted && rowRef) onLoadCompleted(loadCompletedId || 'header', { height: rowRef.offsetHeight });

  }

  render() {

    const { rowRef } = this.state;

    const {
      view,
      id,
      fieldType,
      mode,
      disabled,
      columns,
      fieldPositionColumns,
      columnWidths = {},

      enableNumber,
      enableMenu,

      //Action handlers
      onColumnRef,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      borderColor,
      borderWidth,
      borderRadius,
      backgroundColor,
      padding
    } = this.props;

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const utilityColumns = [];
    if (enableNumber) utilityColumns.push({ _id: FieldTableCustomColumnIds.rowNumber, type: FieldTableCustomColumnIds.rowNumber, width: 46 });
    if (enableMenu) utilityColumns.push({ _id: FieldTableCustomColumnIds.rowMenu, type: FieldTableCustomColumnIds.rowMenu, width: 20 });

    const formattedColumns = [...utilityColumns, ...columns];
    const filteredColumns = filterHiddenTableColumnsSelector({columns: formattedColumns, fieldPositionColumns, view});

    const columnNodes = filteredColumns.map((column, columnIndex) => {

      /**
       * Handle cell borders for collection vs table field type
       */
      const borders = { borderRight: border, borderBottom: border };
      if (fieldType === FieldTypes.collection && columnIndex === 0) borders.borderLeft = border;
      if (fieldType === FieldTypes.collection) borders.borderTop = border;

      const isFirst = columnIndex === 0;
      const isLast = columnIndex === filteredColumns.length - 1;
      const radiusProps = {};
      if (borderRadius != null) {
        if (isFirst) radiusProps.borderTopLeftRadius = borderRadius;
        if (isLast) radiusProps.borderTopRightRadius = borderRadius;
      }

      return (
        <StyledTH
          key={column._id}
          data-testid={`${id}-${column._id}-${columnIndex}-column-th`}
          ref={(e) => {

            if (
              e
              && onColumnRef 
              && e.offsetWidth
              && column._id !== FieldTableCustomColumnIds.rowNumber
              && column._id !== FieldTableCustomColumnIds.rowMenu
            ) onColumnRef(column._id, e);

          }}
          {...{
            width: column.width,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            backgroundColor,
            padding,
            ...borders,
            ...radiusProps
          }}
        >
          <StyledTitleWrapper>
            <StyledTitle>{column.title}</StyledTitle>
          </StyledTitleWrapper>
        </StyledTH>
      );
    });

    return (
      <StyledRow
        data-testid={`${id}-table-field-header-row`}
        ref={(e) => {
          if (!rowRef && e) this.setState({ rowRef: e });
        }}
        {...{
          border,
          backgroundColor
        }}
      >
        {columnNodes}
      </StyledRow>
    );

  }

}

export default memo(TableHeader);
